/**
 * Wait for a DOM element to appear using MutationObserver.
 */
export function waitForElement(selector, timeoutMs = 5000) {
  return new Promise((resolve, reject) => {
    const el = document.querySelector(selector);
    if (el) return resolve(el);

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => {
      observer.disconnect();
      reject(new Error(`Element ${selector} not found within ${timeoutMs}ms`));
    }, timeoutMs);
  });
}

/**
 * Wait for Redux state to satisfy a predicate.
 */
export function waitForReduxState(store, predicate, timeoutMs = 30000) {
  return new Promise((resolve, reject) => {
    if (predicate(store.getState())) return resolve(store.getState());

    const timeout = setTimeout(() => {
      unsubscribe();
      reject(new Error('Redux state timeout'));
    }, timeoutMs);

    const unsubscribe = store.subscribe(() => {
      const state = store.getState();
      if (predicate(state)) {
        unsubscribe();
        clearTimeout(timeout);
        resolve(state);
      }
    });
  });
}

/**
 * Programmatically fill a controlled React input.
 * Uses the native value setter to bypass React's synthetic event system.
 */
export function fillReactInput(selector, value) {
  const el = document.querySelector(selector);
  if (!el) return false;

  // Pick the correct native setter based on element type
  const proto = el instanceof HTMLTextAreaElement
    ? window.HTMLTextAreaElement.prototype
    : window.HTMLInputElement.prototype;
  const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (!nativeSetter) return false;

  nativeSetter.call(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

/**
 * Returns region-specific content based on user's country preference.
 */
export function getRegionContent(countryPref) {
  const primary = Array.isArray(countryPref) ? countryPref[0] : countryPref || 'MA';
  const isUAE = primary === 'AE';

  return {
    countryCode: primary,
    countryFlag: isUAE ? '🇦🇪' : '🇲🇦',
    countryName: isUAE ? 'UAE' : 'Morocco',
    searchDemoQuery: isUAE ? 'UAE labour law' : 'gage et nantissement',
    chatDemoQuery: isUAE
      ? 'What are employee rights in case of unfair dismissal under UAE law?'
      : 'Quels sont les droits du salarié en cas de licenciement abusif au Maroc ?',
    jurisdictionLabel: isUAE ? 'UAE legal system 🇦🇪' : 'Moroccan legal system 🇲🇦',
  };
}
